import { useEffect } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tutorial.css';
import { useMapStore } from '../../store/useMapStore';
import { useTranslation } from '../../i18n/translations';

const TutorialGuide = () => {
  const { t, language } = useTranslation();
  const hasSeenTutorial = useMapStore(state => state.hasSeenTutorial);
  const setHasSeenTutorial = useMapStore(state => state.setHasSeenTutorial);
  const isTutorialOpen = useMapStore(state => state.isTutorialOpen);
  const setIsTutorialOpen = useMapStore(state => state.setIsTutorialOpen);
  const setSidebarOpen = useMapStore(state => state.setSidebarOpen);

  const renderMascotDescription = (desc: string, mascotType: string = 'standard') => {
    let mascotSrc = '/branding/mascot.webp';
    if (mascotType === 'vanakkam') mascotSrc = '/branding/vanakkam-mascot.webp';
    if (mascotType === 'location') mascotSrc = '/branding/location-mascot.webp';
    
    return `
      <div class="tutorial-popover-content">
        <div class="tutorial-mascot-wrapper ${mascotType}">
          <img src="${mascotSrc}" alt="N-Bot Mascot" class="tutorial-mascot-img" />
        </div>
        <div class="tutorial-description-text">${desc}</div>
      </div>
    `;
  };

  useEffect(() => {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      stagePadding: 5,
      popoverClass: 'driverjs-theme',
      overlayColor: '#000000',
      overlayOpacity: 0.65,
      nextBtnText: language === 'ta' ? 'அடுத்து' : 'Next',
      prevBtnText: language === 'ta' ? 'முந்தையது' : 'Previous',
      doneBtnText: language === 'ta' ? 'முடிந்தது' : 'Done',
      onDestroyed: () => {
        setHasSeenTutorial(true);
        setIsTutorialOpen(false);
      },
      steps: [
        { 
          popover: { 
            title: t('TUTORIAL_WELCOME_TITLE'), 
            description: renderMascotDescription(t('TUTORIAL_WELCOME_DESC'), 'vanakkam'),
            align: 'center'
          } 
        },
        { 
          element: '.sidebar', 
          popover: { 
            title: t('TUTORIAL_LAYERS_TITLE'), 
            description: renderMascotDescription(t('TUTORIAL_LAYERS_DESC'), 'standard'),
            side: "right",
            align: 'start'
          },
          onHighlightStarted: () => {
            setSidebarOpen(true);
          }
        },
        { 
          element: '.search-bar', 
          popover: { 
            title: t('TUTORIAL_SEARCH_TITLE'), 
            description: renderMascotDescription(t('TUTORIAL_SEARCH_DESC'), 'standard'),
            side: "bottom",
            align: 'center'
          } 
        },
        { 
          element: '[aria-label="Locate me"]', 
          popover: { 
            title: t('TUTORIAL_LOCATE_TITLE'), 
            description: renderMascotDescription(t('TUTORIAL_LOCATE_DESC'), 'location'),
            side: "bottom",
            align: 'center'
          } 
        },
        { 
          element: '.leaflet-container', 
          popover: { 
            title: t('TUTORIAL_MAP_TITLE'), 
            description: renderMascotDescription(t('TUTORIAL_MAP_DESC'), 'standard'),
            side: "left",
            align: 'center'
          } 
        }
      ]
    });

    if (isTutorialOpen || !hasSeenTutorial) {
      // Delay slightly to ensure layout is settled
      const timer = setTimeout(() => {
        // Prevent re-driving if already driving
        if (document.querySelector('.driver-popover')) return;
        driverObj.drive();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isTutorialOpen, hasSeenTutorial, setIsTutorialOpen, setHasSeenTutorial, setSidebarOpen, t, language]);

  return null;
};

export default TutorialGuide;
